import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import TagManager from 'react-gtm-module';

import ErrorPage from 'pages/ErrorPage';
import HomeComponent from 'pages/HomePage';
import NotFoundPage from 'pages/NotFoundPage';
import RedirectionPage from 'pages/RedirectionPage';
import SplashScreen from 'pages/SplashScreen';
import WelcomePage from 'pages/WelcomePage';
import WelcomeComponent from 'pages/SetPasswordPage/welcome';
import Authentication from 'pages/Authentication';
import MfaSetupPage from 'pages/Authentication/MfaSetupPage';
import MfaTotpPage from 'pages/Authentication/MfaTotpPage';
import ChangePasswordPage from 'pages/Authentication/ChangePasswordPage';
import SetPasswordPage from 'pages/Authentication/SetPasswordPage';
import InvitaionModal from 'pages/Authentication/InvitaionModal';
import { getUserData } from 'redux/UserSlice';
import ModelOverviewPage from 'pages/ModelOverviewPage';
import ProtectedRoutes from 'utils/ProtectedRoutes';
// import PlanPageOutputOptimization from 'pages/PlanPageOutputOptimization';
import PlanPageDetailsOPZ from 'pages/PlanPageDetailsOPZ';
// import PlanPageDetailsOPZRevamp from 'pages/PlanPageDetailsOPZRevamp';
// Confirmed via a temporary swap to V1 that the empty hover dropdowns aren't
// caused by anything in this new header: V1 (untouched, raw LD flags, no
// override helper) showed the exact same empty state, so the cause is
// upstream — LaunchDarkly's `blockDimDropdown`/`dataImportDropdown` flags
// for this environment, or FetchFormulaList's API response.
import { ModalOverViewPageHeaderRevamp } from 'pages/ModalOverViewPageHeaderRevamp';
import { Flags } from 'models/Global.interface';

interface NavigationProperties {
  flags?: Flags;
}

const ModelHomeRedirect = () => {
  const { modelId } = useParams();

  if (!modelId) {
    return <Navigate to='/home' replace />;
  }

  return <Navigate to={`/model/${modelId}/dashboard`} replace />;
};

const ModelTabRedirect = () => {
  const { modelId, tab } = useParams();
  const tabMap: Record<string, string> = {
    dashboard: 'dashboard',
    blocks: 'blocks',
    dimensions: 'dimensions',
    data: 'data',
    import: 'data',
  };

  if (!modelId) {
    return <Navigate to='/home' replace />;
  }

  const normalizedTab = tabMap[tab || ''] ?? 'dashboard';
  return <Navigate to={`/model/${modelId}/${normalizedTab}`} replace />;
};

const Navigation = ({ flags }: NavigationProperties) => {
  const { REACT_APP_GTM_ID }: any = process.env;
  const location = useLocation();
  const user = useSelector(getUserData);

  useEffect(() => {
    if (user) {
      const tagManagerArguments = {
        gtmId: REACT_APP_GTM_ID,
        events: {
          userId: user.id,
        },
      };
      TagManager.initialize(tagManagerArguments);
    }
  }, [location.pathname]);

  return (
    <Routes>
      <Route index element={<Authentication type={1} />} />
      <Route path='signup' element={<Authentication type={2} />} />
      <Route path='mfa/setup' element={<MfaSetupPage />} />
      <Route path='mfa/totp' element={<MfaTotpPage />} />
      <Route path='create/:modelId' element={<SplashScreen />} />
      <Route
        path='welcome'
        element={
          <ProtectedRoutes>
            <WelcomePage />
          </ProtectedRoutes>
        }
      />
      <Route path='change-password' element={<ChangePasswordPage />} />
      <Route path='set-password' element={<SetPasswordPage />} />
      <Route path='set-new-password' element={<SetPasswordPage />} />
      <Route path='user/welcome' element={<WelcomeComponent />} />
      <Route
        path='home'
        element={
          <ProtectedRoutes>
            <HomeComponent />
          </ProtectedRoutes>
        }
      />
      <Route path='share-model/:token/:emailToken' element={<RedirectionPage />} />
      <Route path='share-model/:token' element={<RedirectionPage />} />

      <Route
        path='model/:modelId'
        element={
          <ProtectedRoutes>
            <Navigate to='dashboard' replace />
          </ProtectedRoutes>
        }
      />
      <Route
        path='model/:modelId/:tab'
        element={
          <ProtectedRoutes>
            {flags?.uiRevamp ? <ModalOverViewPageHeaderRevamp /> : <ModelOverviewPage />}
          </ProtectedRoutes>
        }
      />
      <Route
        path='model/:modelId/tab=:tab'
        element={
          <ProtectedRoutes>
            <ModelTabRedirect />
          </ProtectedRoutes>
        }
      />
      <Route
        path='model/:modelId/home'
        element={
          <ProtectedRoutes>
            {flags?.uiRevamp ? <ModelHomeRedirect /> : <ModelOverviewPage />}
          </ProtectedRoutes>
        }
      />
      <Route
        path='model/:modelId/block/:blockId/plan'
        element={
          <ProtectedRoutes>
            <PlanPageDetailsOPZ flags={flags as Flags} />
            {/* {flags?.detailsApiRevamp ? <PlanPageDetailsOPZ /> : <PlanPageOutputOptimization />} */}
          </ProtectedRoutes>
        }
      />
      <Route
        path='/error'
        element={
          <ProtectedRoutes>
            <ErrorPage />
          </ProtectedRoutes>
        }
      />
      <Route path='/invitation/:token' element={<InvitaionModal />} />
      <Route path='/*' element={<NotFoundPage />} />
    </Routes>
  );
};

export default withLDConsumer()(Navigation);
